import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

import { dismissBanner } from '../../store/modules/migration/actions';
import {
  getProgressPercent,
  getStepLabel,
  V2_DASHBOARD_URL,
  canAccessMigration,
} from '../../utils/migration';

function MigrationBanner() {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { phase, migrationData, bannerDismissed } = useSelector(
    state => state.migration
  );
  const userProfile = useSelector(state => state.user.profile);

  if (phase === 'idle') return null;
  if (bannerDismissed) return null;
  if (location.pathname === '/migration') return null;
  if (!canAccessMigration(userProfile)) return null;

  const progress = migrationData
    ? getProgressPercent(migrationData.completedSteps)
    : 0;
  const stepLabel = migrationData?.currentStep
    ? getStepLabel(migrationData.currentStep)
    : 'Aguardando...';

  function handleDismiss(e) {
    e.stopPropagation();
    dispatch(dismissBanner());
  }

  function handleClick() {
    history.push('/migration');
  }

  if (phase === 'in_progress') {
    return (
      <>
        <Banner
          bg="#2B8FF5"
          onClick={handleClick}
          style={{ cursor: 'pointer' }}
        >
          <BannerContent>
            <MiniSpinner />
            <BannerText>
              Migração em andamento - {stepLabel} {progress}%
            </BannerText>
            <MiniProgressBarWrapper>
              <MiniProgressBarFill style={{ width: `${progress}%` }} />
            </MiniProgressBarWrapper>
          </BannerContent>
        </Banner>
        <Spacer />
      </>
    );
  }

  if (phase === 'completed') {
    return (
      <>
        <Banner bg="#2d8f2d">
          <BannerContent>
            <BannerText>
              Migração concluída!{' '}
              <BannerLink
                href={V2_DASHBOARD_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Acessar nova versão →
              </BannerLink>
            </BannerText>
            <CloseButton type="button" onClick={handleDismiss}>
              ✕
            </CloseButton>
          </BannerContent>
        </Banner>
        <Spacer />
      </>
    );
  }

  if (phase === 'failed') {
    return (
      <>
        <Banner
          bg="#cc3333"
          onClick={handleClick}
          style={{ cursor: 'pointer' }}
        >
          <BannerContent>
            <BannerText>
              Erro na migração - Clique para tentar novamente
            </BannerText>
            <CloseButton type="button" onClick={handleDismiss}>
              ✕
            </CloseButton>
          </BannerContent>
        </Banner>
        <Spacer />
      </>
    );
  }

  return null;
}

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Banner = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 40px;
  z-index: 600;
  background: ${props => props.bg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', sans-serif;
`;

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  width: 100%;
  max-width: 720px;
`;

const BannerText = styled.span`
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;

const BannerLink = styled.a`
  color: #fff;
  font-weight: 600;
  text-decoration: underline;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  cursor: pointer;
  padding: 4px 8px;
  flex-shrink: 0;

  &:hover {
    color: #fff;
  }
`;

const MiniSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  flex-shrink: 0;
`;

const MiniProgressBarWrapper = styled.div`
  width: 60px;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  overflow: hidden;
  flex-shrink: 0;
`;

const MiniProgressBarFill = styled.div`
  height: 100%;
  background: #fff;
  border-radius: 2px;
  transition: width 0.5s ease;
`;

const Spacer = styled.div`
  height: 40px;
`;

export default MigrationBanner;
